import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Offcanvas,
    OffcanvasHeader, 
    OffcanvasBody, 
    FormGroup, 
    Label, 
    Input, 
    Button, 
    Spinner,
    Row,
    Col
} from "reactstrap";
import usePlatformLeadDetail from "../../hooks/usePlatformLeadDetail";
import PlatformLeadTimeline from "./PlatformLeadTimeline";
import PlatformStageTag from "./PlatformStageTag";
import PlatformTemperatureBadge from "./PlatformTemperatureBadge";
import { CONTACT_OUTCOMES, LOSS_REASONS, REOPEN_REASONS, REOPENABLE_STAGES, SALES_STAGES, STAGE_CONSULTANT_PLAYBOOK, getLeadScoreTier } from "../../constants";

const PlatformLeadSidebar = ({ isOpen, toggle, leadId, consultants = [], onActionComplete }) => {
    const navigate = useNavigate();
    const {
        lead,
        interactions,
        loading,
        changeStage,
        addOutcome,
        assignConsultantToLead,
        reopenLead,
        archiveLead
    } = usePlatformLeadDetail(leadId);

    // Estado do formulário de contato
    const [channel, setChannel] = useState("whatsapp");
    const [outcome, setOutcome] = useState("interested");
    const [notes, setNotes] = useState("");
    const [lossReason, setLossReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Estado de alteração manual de estágio
    const [manualStage, setManualStage] = useState("");

    // Estado do formulário de reabertura
    const [showReopenForm, setShowReopenForm] = useState(false);
    const [reopenReason, setReopenReason] = useState("");
    const [reopenNotes, setReopenNotes] = useState("");
    const [reopening, setReopening] = useState(false);

    useEffect(() => {
        if (lead) {
            setManualStage(lead.salesData?.salesStage || "");
        }
    }, [lead]);

    // Reseta form ao abrir/fechar ou mudar de lead
    useEffect(() => {
        setChannel("whatsapp");
        setOutcome("interested");
        setNotes("");
        setLossReason("");
    }, [leadId, isOpen]);

    const handleAssignConsultant = async (e) => {
        const selectedId = e.target.value;
        if (!selectedId) {
            await assignConsultantToLead(null);
        } else {
            const selected = consultants.find(c => c.id === selectedId);
            if (selected) {
                await assignConsultantToLead({ uid: selected.id, name: selected.fullName });
            }
        }
        if (onActionComplete) onActionComplete();
    };

    const handleStageChange = async (e) => {
        const target = e.target.value;
        if (target && target !== lead.salesData?.salesStage) {
            setManualStage(target);
            await changeStage(target, `Mapeado de estágio manualmente para: ${target}`);
            if (onActionComplete) onActionComplete();
        }
    };

    const handleReopen = async (e) => {
        e.preventDefault();
        if (!reopenReason) return;
        try {
            setReopening(true);
            await reopenLead({ reopenReason, notes: reopenNotes });
            setShowReopenForm(false);
            setReopenReason("");
            setReopenNotes("");
            if (onActionComplete) onActionComplete();
        } catch (err) {
            console.error(err);
        } finally {
            setReopening(false);
        }
    };

    const handleArchive = async () => {
        if (!window.confirm("Arquivar este lead? Ele ficará oculto do Kanban principal, mas pode ser recuperado pelo Arquivo.")) return;
        try {
            await archiveLead();
            if (onActionComplete) onActionComplete();
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmitContact = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            await addOutcome({
                outcome,
                notes,
                channel,
                lossReason: outcome === "lost" ? lossReason : null
            });
            setNotes("");
            setLossReason("");
            if (onActionComplete) onActionComplete();
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!lead && loading) {
        return (
            <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" className="crm-offcanvas">
                <OffcanvasBody className="d-flex align-items-center justify-content-center">
                    <Spinner color="primary" />
                </OffcanvasBody>
            </Offcanvas>
        );
    }

    if (!lead) return null;

    const owner = lead.owner || {};
    const cleanPhone = lead.phone ? lead.phone.replace(/\D/g, "") : "";
    const waUrl = cleanPhone ? `https://wa.me/${cleanPhone.startsWith("55") ? cleanPhone : "55" + cleanPhone}` : null;

    const currentStage = lead.salesData?.salesStage;
    const score = lead.salesData?.leadScore;
    const scoreTier = score !== null && score !== undefined ? getLeadScoreTier(score) : null;
    const playbook = STAGE_CONSULTANT_PLAYBOOK[currentStage] || null;
    const isWon = currentStage === "won";

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" className="crm-offcanvas">
            <OffcanvasHeader toggle={toggle} className="crm-offcanvas-header p-3">
                <div className="d-flex flex-column align-items-start gap-1">
                    <span className="crm-offcanvas-subtitle">CRM de Leads B2B</span>
                    <h5 className="mb-0 text-white fw-bold font-size-16">{lead.name}</h5>
                </div>
            </OffcanvasHeader>
            <OffcanvasBody className="crm-offcanvas-body p-4">
                
                {/* 1. Visão Geral do Status */}
                <div className="d-flex align-items-center justify-content-between mb-4 bg-white p-3 rounded shadow-sm">
                    <div>
                        <small className="text-muted d-block mb-1 font-size-11 uppercase fw-bold">Estágio Atual</small>
                        <PlatformStageTag stageKey={lead.salesData?.salesStage} />
                    </div>
                    {scoreTier && (
                        <div className="text-center">
                            <small className="text-muted d-block mb-1 font-size-11 uppercase fw-bold">Lead Score</small>
                            <span
                                className="badge fw-bold font-size-13"
                                style={{ color: scoreTier.color, backgroundColor: scoreTier.bg }}
                                title={scoreTier.description}
                            >
                                {score}/100 · {scoreTier.label}
                            </span>
                        </div>
                    )}
                    <div className="text-end">
                        <small className="text-muted d-block mb-1 font-size-11 uppercase fw-bold">Temperatura</small>
                        <PlatformTemperatureBadge temperature={lead.salesData?.temperature} />
                    </div>
                </div>

                {/* 1.2 Lead ganho → acompanhar implantação */}
                {isWon && (
                    <div className="bg-white p-3 rounded shadow-sm mb-4 border-start border-success border-4">
                        <h6 className="fw-bold text-success mb-2 font-size-13">
                            <i className="mdi mdi-party-popper me-1" />
                            Cliente ganho!
                        </h6>
                        <p className="text-muted font-size-12 mb-3">
                            O onboarding foi criado automaticamente. Acompanhe a implantação para garantir a ativação e evitar churn precoce.
                        </p>
                        <Button
                            color="success"
                            size="sm"
                            className="w-100 fw-semibold"
                            onClick={() => navigate(`/platform/onboarding?tenant=${leadId}`)}
                        >
                            <i className="mdi mdi-clipboard-text-play-outline me-1" />
                            Acompanhar Implantação
                        </Button>
                    </div>
                )}

                {/* 1.3 Playbook do estágio — orientação do consultor */}
                {playbook && !isWon && (
                    <div className="bg-white p-3 rounded shadow-sm mb-4 border-start border-warning border-4">
                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">
                            <i className="mdi mdi-playlist-check me-1 text-warning" />
                            Playbook do Estágio
                        </h6>
                        <div className="mb-2">
                            <small className="text-muted d-block font-size-10 uppercase fw-bold">SLA</small>
                            <span className="font-size-12 text-dark fw-semibold">{playbook.sla}</span>
                        </div>
                        <div className="mb-2">
                            <small className="text-muted d-block font-size-10 uppercase fw-bold">Objetivo</small>
                            <span className="font-size-12 text-dark">{playbook.goal}</span>
                        </div>
                        <div className="mb-2">
                            <small className="text-muted d-block font-size-10 uppercase fw-bold">Ação Recomendada</small>
                            <span className="font-size-12 text-dark">{playbook.action}</span>
                        </div>
                        <div className="p-2 rounded" style={{ backgroundColor: "rgba(239, 68, 68, 0.06)" }}>
                            <small className="text-danger font-size-11 fw-semibold">
                                <i className="mdi mdi-alert-outline me-1" />
                                {playbook.redFlag}
                            </small>
                        </div>
                    </div>
                )}

                {/* 1.5 Atalho para o cadastro completo do cliente */}
                <Button
                    color="primary"
                    outline
                    size="sm"
                    className="w-100 mb-4 fw-semibold"
                    onClick={() => navigate(`/platform/tenants/${leadId}`)}
                >
                    <i className="mdi mdi-office-building-outline me-1" />
                    Ver Cadastro Completo do Cliente
                </Button>

                {/* 2. Informações de Contato */}
                <div className="bg-white p-3 rounded shadow-sm mb-4">
                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">Dados do Lead</h6>
                    <Row className="gy-2 mb-2">
                        <Col xs={6}>
                            <small className="text-muted d-block font-size-11">Responsável / Dono</small>
                            <span className="fw-semibold font-size-13 text-dark">{lead.ownerName || owner.fullName || "Dono não cadastrado"}</span>
                        </Col>
                        <Col xs={6}>
                            <small className="text-muted d-block font-size-11">E-mail</small>
                            <span className="font-size-13 text-dark text-break">{lead.ownerEmail || lead.email || "E-mail não cadastrado"}</span>
                        </Col>
                    </Row>
                    <Row className="gy-2 mb-1">
                        <Col xs={6}>
                            <small className="text-muted d-block font-size-11">Telefone / WhatsApp</small>
                            <div className="d-flex align-items-center gap-2">
                                <span className="font-size-13 text-dark">{lead.ownerPhone || lead.phone || "Sem Telefone"}</span>
                                {waUrl && (
                                    <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-soft-success p-1 rounded-circle crm-offcanvas-whatsapp-btn" title="Conversar no WhatsApp">
                                        <i className="mdi mdi-whatsapp"></i>
                                    </a>
                                )}
                            </div>
                        </Col>
                        <Col xs={6}>
                            <small className="text-muted d-block font-size-11">Origem do Lead</small>
                            <span className="font-size-13 text-dark uppercase fw-semibold">{lead.salesData?.source || "Cadastro Checkout"}</span>
                        </Col>
                    </Row>
                </div>

                {/* 3. Atribuição & Ações Rápidas */}
                <div className="bg-white p-3 rounded shadow-sm mb-4">
                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">Atribuição e Estágio</h6>
                    
                    <FormGroup className="mb-3">
                        <Label className="font-size-12 fw-semibold text-muted">Consultor Responsável</Label>
                        <Input
                            type="select"
                            value={lead.salesData?.assignedTo?.uid || ""}
                            onChange={handleAssignConsultant}
                            className="form-select font-size-13"
                        >
                            <option value="">Não atribuído (Disponível para a equipe)</option>
                            {consultants.map(c => (
                                <option key={c.id} value={c.id}>{c.fullName}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Label className="font-size-12 fw-semibold text-muted">Mover Estágio Manualmente</Label>
                        <Input
                            type="select"
                            value={manualStage}
                            onChange={handleStageChange}
                            className="form-select font-size-13"
                        >
                            {SALES_STAGES.map(s => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </div>

                {/* 3.5 Reabertura / Arquivo */}
                {(REOPENABLE_STAGES.includes(lead.salesData?.salesStage) || lead.salesData?.isArchived) && (
                    <div className="bg-white p-3 rounded shadow-sm mb-4">
                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">
                            <i className="mdi mdi-refresh me-1 text-warning" />
                            Reabertura de Lead
                        </h6>

                        {lead.salesData?.reEngagementCount > 0 && (
                            <p className="text-muted font-size-11 mb-3">
                                <i className="mdi mdi-history me-1" />
                                Este lead foi reaberto {lead.salesData.reEngagementCount}× anteriormente.
                            </p>
                        )}

                        {!showReopenForm ? (
                            <div className="d-flex gap-2">
                                <Button
                                    color="warning"
                                    size="sm"
                                    outline
                                    className="flex-grow-1 fw-semibold"
                                    onClick={() => setShowReopenForm(true)}
                                >
                                    <i className="mdi mdi-refresh me-1" />
                                    Reabrir Lead
                                </Button>
                                {!lead.salesData?.isArchived && (
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        outline
                                        onClick={handleArchive}
                                        title="Arquivar lead (ocultar do Kanban)"
                                    >
                                        <i className="mdi mdi-archive-outline" />
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleReopen}>
                                <FormGroup className="mb-2">
                                    <Label className="font-size-11 fw-semibold text-muted">Motivo da Reabertura</Label>
                                    <Input
                                        type="select"
                                        value={reopenReason}
                                        onChange={(e) => setReopenReason(e.target.value)}
                                        className="form-select font-size-12"
                                        required
                                    >
                                        <option value="">Selecione o motivo...</option>
                                        {REOPEN_REASONS.map(r => (
                                            <option key={r.key} value={r.key}>{r.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                                <FormGroup className="mb-3">
                                    <Label className="font-size-11 fw-semibold text-muted">Observações (opcional)</Label>
                                    <Input
                                        type="textarea"
                                        rows="2"
                                        value={reopenNotes}
                                        onChange={(e) => setReopenNotes(e.target.value)}
                                        placeholder="Contexto ou detalhe sobre a reabertura..."
                                        className="form-control font-size-12"
                                    />
                                </FormGroup>
                                <div className="d-flex gap-2 justify-content-end">
                                    <Button size="sm" color="secondary" outline onClick={() => setShowReopenForm(false)}>
                                        Cancelar
                                    </Button>
                                    <Button size="sm" color="warning" type="submit" disabled={reopening || !reopenReason}>
                                        {reopening ? "Reabrindo..." : "Confirmar Reabertura"}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                )}

                {/* 4. Registrar Nova Interação */}
                <div className="bg-white p-3 rounded shadow-sm mb-4">
                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">Registrar Novo Contato</h6>
                    
                    <form onSubmit={handleSubmitContact}>
                        <Row className="mb-3">
                            <Col xs={6}>
                                <Label className="font-size-11 fw-semibold text-muted">Canal de Contato</Label>
                                <Input
                                    type="select"
                                    value={channel}
                                    onChange={(e) => setChannel(e.target.value)}
                                    className="form-select font-size-12"
                                    required
                                >
                                    <option value="whatsapp">WhatsApp</option>
                                    <option value="phone">Ligação Telefônica</option>
                                    <option value="email">E-mail</option>
                                    <option value="in_person">Contato Presencial</option>
                                </Input>
                            </Col>
                            <Col xs={6}>
                                <Label className="font-size-11 fw-semibold text-muted">Resultado</Label>
                                <Input
                                    type="select"
                                    value={outcome}
                                    onChange={(e) => setOutcome(e.target.value)}
                                    className="form-select font-size-12"
                                    required
                                >
                                    {CONTACT_OUTCOMES.map(o => (
                                        <option key={o.key} value={o.key}>{o.label}</option>
                                    ))}
                                </Input>
                            </Col>
                        </Row>

                        {outcome === "lost" && (
                            <FormGroup className="mb-3">
                                <Label className="font-size-11 fw-semibold text-muted">Motivo da Perda</Label>
                                <Input
                                    type="select"
                                    value={lossReason}
                                    onChange={(e) => setLossReason(e.target.value)}
                                    className="form-select font-size-12"
                                    required
                                >
                                    <option value="">Selecione o motivo...</option>
                                    {LOSS_REASONS.map(r => (
                                        <option key={r.key} value={r.key}>{r.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        )}

                        <FormGroup className="mb-3">
                            <Label className="font-size-11 fw-semibold text-muted">Observações / Detalhes</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Descreva os pontos principais da conversa..."
                                className="form-control font-size-12"
                                required
                            />
                        </FormGroup>

                        <div className="text-end">
                            <Button 
                                type="submit" 
                                color="primary" 
                                size="sm" 
                                disabled={submitting} 
                                className="crm-offcanvas-submit-btn"
                            >
                                {submitting ? "Registrando..." : "Registrar Contato"}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* 5. Histórico de Interações (Timeline) */}
                <div className="bg-white p-3 rounded shadow-sm">
                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-3 font-size-13">Histórico / Timeline</h6>
                    <PlatformLeadTimeline interactions={interactions} />
                </div>

            </OffcanvasBody>
        </Offcanvas>
    );
};

export default PlatformLeadSidebar;
